import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Combobox } from "@/components/Combobox";
import { BodyMapView } from "@/components/BodyMapView";
import { ArrowLeft, Loader2, Download, ShieldAlert, Play, RotateCcw, CheckCircle2, RefreshCw, Send, FileText, ImageOff, X, Lock, ListChecks, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtLog } from "@/lib/tz";
import { orgToday } from "@/lib/tz";

const SEV = { Low: "bg-slate-100 text-slate-600", Medium: "bg-rose-100 text-[#bf3b4f]", High: "bg-orange-100 text-orange-700", Critical: "bg-red-100 text-red-700" };

function BodyMapHasPins(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string" && value.trim()) {
    try { const p = JSON.parse(value); return Array.isArray(p) && p.length > 0; } catch { return false; }
  }
  return false;
}
const STA = { Submitted: "bg-amber-100 text-amber-800", "Under Review": "bg-rose-100 text-[#bf3b4f]", Returned: "bg-purple-100 text-purple-700", Closed: "bg-slate-100 text-slate-500" };
const dl = (blob, fn) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = fn; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); };
const KV = ({ k, v }) => (<div><div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div><div className="text-sm text-slate-700">{v || "—"}</div></div>);
const Block = ({ title, text }) => text ? (<div className="mb-2"><div className="text-[10px] uppercase tracking-wide text-slate-400">{title}</div><div className="text-sm text-slate-700 whitespace-pre-wrap">{text}</div></div>) : null;

export default function IncidentDetail() {
  const { name } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [inc, setInc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [staff, setStaff] = useState([]);
  const manage = can.has("incidents.manage");
  const canReview = can.has("incidents.review");
  const canClose = can.has("incidents.close");

  const load = () => api.incident(name).then(setInc).catch(() => toast.error("Could not load incident"));
  useEffect(() => { load(); }, [name]); // eslint-disable-line
  useEffect(() => { api.staff().then(setStaff).catch(() => {}); }, []);

  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };
  const setFlag = (field, value) => act(() => api.incidentFlags(name, { [field]: value }), "Updated");
  const addNote = async () => { if (!note.trim()) return; await act(() => api.incidentNote(name, note.trim()), "Note added"); setNote(""); };
  const downloadPdf = async () => { try { const b = await api.incidentPdf(name); dl(b, `Incident-${name}.pdf`); } catch { toast.error("Could not generate PDF"); } };

  if (!inc) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const isOpen = inc.status !== "Closed";
  const openActionCount = (inc.corrective_actions || []).filter((a) => a.status === "Open" || a.status === "In Progress").length;

  return (
    <div className="p-4 max-w-5xl">
      <button onClick={() => nav("/incidents")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2" data-testid="incident-back"><ArrowLeft className="h-3.5 w-3.5" /> All incidents</button>

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <div>
            <h1 className="text-lg font-bold text-slate-900 font-mono">{inc.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${SEV[inc.severity]}`}>{inc.severity}</span>
              <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${STA[inc.status]}`} data-testid="incident-status">{inc.status}</span>
              {inc.reportable ? <span className="rounded bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 text-[9px] font-semibold">NDIS reportable</span> : null}
              {inc.notifiable_stakeholders ? <span className="rounded bg-emerald-50 text-[#3f9963] border border-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold">Stakeholder-notifiable</span> : null}
            </div>
          </div>
        </div>
        {can.has("incidents.reports") && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={downloadPdf} data-testid="incident-pdf"><Download className="h-3.5 w-3.5 mr-1.5" />Overview PDF</Button>}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Left: incident content */}
        <div className="md:col-span-2 space-y-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-3">
              <KV k="Participant" v={inc.participant_name || inc.participant} />
              <KV k="Support worker" v={inc.support_worker_name || inc.support_worker} />
              <KV k="Incident date/time" v={inc.incident_datetime ? dayjs(inc.incident_datetime).format("D MMM YYYY, HH:mm") : "—"} />
              <KV k="Location" v={inc.location} />
              <KV k="Shift" v={inc.shift} />
              <KV k="Types" v={(inc.types || []).join(", ")} />
              <KV k="Reported by" v={inc.source === "Office" ? `${inc.reported_by || "—"} (office)` : inc.reported_by} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-bold text-slate-800 mb-2">What happened</div>
            <Block title="Leading up to the incident" text={inc.leading_up} />
            <Block title="Description" text={inc.description} />
            <Block title="Immediate action taken" text={inc.immediate_action} />
            <Block title="Witnesses" text={inc.witnesses} />
          </div>

          {(inc.behaviour_details || inc.self_harm_details || inc.medication_details || inc.vehicle_details || inc.other_details) && (
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-bold text-slate-800 mb-2">Type details</div>
              <Block title="Behaviour" text={inc.behaviour_details} />
              <Block title="Self harm" text={inc.self_harm_details} />
              <Block title="Medication" text={inc.medication_details} />
              <Block title="Vehicle" text={inc.vehicle_details} />
              {inc.vehicle_rego ? <Block title="Vehicle rego" text={`${inc.vehicle_rego}${inc.police_attended ? ` · Police attended${inc.police_report_number ? " · Report " + inc.police_report_number : ""}` : ""}`} /> : null}
              <Block title="Other" text={inc.other_details} />
            </div>
          )}

          {(inc.injuries || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-bold text-slate-800 mb-2">Injuries</div>
              <table className="w-full text-[11px]">
                <thead><tr className="text-left text-slate-400"><th className="py-1">Person</th><th>Location</th><th>Nature</th><th>First aid</th><th>Med/Hosp</th></tr></thead>
                <tbody>{inc.injuries.map((j, i) => (
                  <tr key={i} className="border-t border-slate-50"><td className="py-1">{j.person_type} {j.person_name}</td><td>{j.body_location || "—"}</td><td>{j.nature_of_injury || "—"}</td><td>{j.first_aid_given || "—"}</td><td>{j.medical_treatment_sought ? "Med" : ""}{j.hospital ? " Hosp" : ""}{!j.medical_treatment_sought && !j.hospital ? "—" : ""}</td></tr>
                ))}</tbody>
              </table>
              {inc.injuries.some((j) => BodyMapHasPins(j.body_map)) && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid="incident-body-maps">
                  <div className="text-[11px] font-bold text-slate-700">Injury body map</div>
                  {inc.injuries.map((j, i) => BodyMapHasPins(j.body_map) && (
                    <div key={i} className="flex items-start gap-3">
                      <BodyMapView value={j.body_map} testid={`incident-bodymap-${i}`} />
                      <div className="text-[11px] text-slate-500 pt-5">
                        <span className="font-semibold text-slate-700">{j.person_type} {j.person_name}</span>
                        {j.body_location ? <> · {j.body_location}</> : null}
                        {j.nature_of_injury ? <> · {j.nature_of_injury}</> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(inc.photos || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-xs font-bold text-slate-800 mb-2">Photos <span className="font-normal text-slate-400">({inc.photos.length})</span></div>
              <IncidentPhotos incidentName={inc.name} photos={inc.photos} />
            </div>
          )}
        </div>

        {/* Right: management */}
        <div className="space-y-3">
          {manage || canReview || canClose ? (
            <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="incident-manage">
              <div className="text-xs font-bold text-slate-800 mb-2">Manage</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {canReview && (inc.status === "Submitted" || inc.status === "Returned") && <Button size="sm" className="h-7 text-[11px] bg-[#bf3b4f] hover:bg-[#a32f42]" onClick={() => act(() => api.incidentReview(name), "Review started")} disabled={busy} data-testid="incident-review"><Play className="h-3 w-3 mr-1" />Start review</Button>}
                {manage && isOpen && inc.source !== "Office" && <Button size="sm" variant="outline" className="h-7 text-[11px] text-purple-700 border-purple-200" onClick={() => setReturnOpen(true)} disabled={busy} data-testid="incident-return"><RotateCcw className="h-3 w-3 mr-1" />Return to worker</Button>}
                {canClose && isOpen && <Button size="sm" variant="outline" className="h-7 text-[11px] text-teal-700 border-teal-200" onClick={() => act(() => api.incidentClose(name), "Incident closed")} disabled={busy || openActionCount > 0} title={openActionCount > 0 ? `${openActionCount} corrective action${openActionCount > 1 ? "s" : ""} still open — complete or cancel ${openActionCount > 1 ? "them" : "it"} first` : undefined} data-testid="incident-close"><CheckCircle2 className="h-3 w-3 mr-1" />Close{openActionCount > 0 ? ` (${openActionCount} open action${openActionCount > 1 ? "s" : ""})` : ""}</Button>}
                {canClose && !isOpen && <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setReopenOpen(true)} disabled={busy} data-testid="incident-reopen"><RefreshCw className="h-3 w-3 mr-1" />Reopen</Button>}
              </div>

              {isOpen ? (
              <div className="space-y-2 border-t border-slate-100 pt-2">
                <label className="flex items-center justify-between"><span className="text-xs text-slate-600">Reportable to NDIS</span>
                  <Switch checked={!!inc.reportable} onCheckedChange={(v) => setFlag("reportable", v)} disabled={busy || !manage} data-testid="incident-flag-reportable" /></label>
                {inc.reportable ? <KV k="NDIS notification date" v={inc.notification_date} /> : null}
                <label className="flex items-center justify-between"><span className="text-xs text-slate-600">Notifiable to stakeholders</span>
                  <Switch checked={!!inc.notifiable_stakeholders} onCheckedChange={(v) => setFlag("notifiable_stakeholders", v)} disabled={busy || !manage} data-testid="incident-flag-notifiable" /></label>
                <div>
                  <span className="text-xs text-slate-600">Severity</span>
                  <select value={inc.severity} onChange={(e) => setFlag("severity", e.target.value)} disabled={busy || !manage} data-testid="incident-severity-select"
                    className="mt-1 w-full h-8 text-xs rounded-md border border-slate-200 bg-white px-2">
                    {["Low", "Medium", "High", "Critical"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              ) : (
                <div className="flex items-start gap-2 border-t border-slate-100 pt-2 text-[11px] text-slate-500" data-testid="incident-locked-note">
                  <Lock className="h-3.5 w-3.5 mt-px shrink-0 text-slate-400" />
                  <span>This incident is <b>closed and locked</b>. Reopen it (with a justification) to edit flags, severity or add notes.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-500" data-testid="incident-readonly">
              You have read-only access to incidents. Contact an Incident Manager to action this report.
            </div>
          )}

          {/* Corrective actions */}
          <CorrectiveActions inc={inc} name={name} manage={manage} isOpen={isOpen} staff={staff} reload={load} />

          {/* Management notes */}
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-bold text-slate-800 mb-2">Management notes</div>
            {manage && isOpen && (
              <div className="mb-3">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an investigation note…" className="text-xs min-h-[60px]" data-testid="incident-note-input" />
                <Button size="sm" className="h-7 text-[11px] mt-1.5 w-full bg-slate-800 hover:bg-slate-900" onClick={addNote} disabled={busy || !note.trim()} data-testid="incident-note-add"><Send className="h-3 w-3 mr-1" />Add note</Button>
              </div>
            )}
            <div className="space-y-2" data-testid="incident-notes-log">
              {(inc.management_notes || []).length === 0 ? <div className="text-[11px] text-slate-400">No notes yet.</div> :
                [...(inc.management_notes || [])].reverse().map((n, i) => (
                  <div key={i} className="border-l-2 border-[#bf3b4f]/40 pl-2">
                    <div className="text-[10px] text-slate-400">{fmtLog(n.noted_at)} · {n.author}</div>
                    <div className="text-xs text-slate-700 whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
            </div>
            {inc.return_reason && inc.status === "Returned" && <div className="mt-2 text-[11px] text-purple-600">Returned to worker: "{inc.return_reason}"</div>}
          </div>

          {/* Activity log (system audit trail — separate from notes) */}
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-bold text-slate-800 mb-2">Activity log</div>
            <div className="space-y-2" data-testid="incident-activity-log">
              {(inc.activity_log || []).length === 0 ? <div className="text-[11px] text-slate-400">No activity yet.</div> :
                [...(inc.activity_log || [])].reverse().map((n, i) => (
                  <div key={i} className="border-l-2 border-slate-200 pl-2">
                    <div className="text-[10px] text-slate-400">{fmtLog(n.noted_at)} · {n.author} · {n.kind}</div>
                    <div className="text-xs text-slate-600 whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={returnOpen} onOpenChange={(v) => !v && setReturnOpen(false)}>
        <DialogContent data-testid="incident-return-dialog">
          <DialogHeader><DialogTitle className="text-base">Return incident to worker</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The worker will be able to edit and resubmit this incident. Give a reason — they'll see it.</p>
          <Textarea autoFocus value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="Reason (required)" className="text-xs min-h-[80px]" data-testid="incident-return-reason" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReturnOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" disabled={busy || !returnReason.trim()} data-testid="incident-return-confirm"
              onClick={async () => { await act(() => api.returnIncident(name, returnReason.trim()), "Returned to worker"); setReturnOpen(false); setReturnReason(""); }}>
              Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={(v) => !v && setReopenOpen(false)}>
        <DialogContent data-testid="incident-reopen-dialog">
          <DialogHeader><DialogTitle className="text-base">Reopen closed incident</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Reopening unlocks this incident for further edits and notes. A justification is required and will be recorded on the audit log.</p>
          <Textarea autoFocus value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Why does this incident need to be reopened? (required)" className="text-xs min-h-[80px]" data-testid="incident-reopen-reason" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={busy || !reopenReason.trim()} data-testid="incident-reopen-confirm"
              onClick={async () => { await act(() => api.incidentReopen(name, reopenReason.trim()), "Incident reopened"); setReopenOpen(false); setReopenReason(""); }}>
              Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorrectiveActions({ inc, name, manage, isOpen, staff, reload }) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ action: "", assigned_to: null, due_date: "", priority: "Medium" });
  const [completeIdx, setCompleteIdx] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const actions = inc.corrective_actions || [];
  const today = orgToday();
  const staffOpts = staff.map((s) => ({ value: s.name, label: s.display_name || s.name }));

  const P = { Low: "bg-slate-100 text-slate-600", Medium: "bg-rose-100 text-[#bf3b4f]", High: "bg-orange-100 text-orange-700" };
  const S = { Open: "bg-amber-100 text-amber-800", "In Progress": "bg-rose-100 text-[#bf3b4f]", Completed: "bg-teal-100 text-teal-700", Cancelled: "bg-slate-100 text-slate-500" };

  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); await reload(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };

  const submitAdd = async () => {
    if (!form.action.trim()) return toast.error("Describe the action");
    if (!form.due_date) return toast.error("Pick a due date");
    await run(() => api.addIncidentAction(name, {
      action: form.action.trim(), assigned_to: form.assigned_to, due_date: form.due_date, priority: form.priority,
    }), "Action added");
    setForm({ action: "", assigned_to: null, due_date: "", priority: "Medium" }); setAdding(false);
  };

  const setStatus = (idx, status) => run(() => api.updateIncidentAction(name, idx, { status }), "Updated");
  const complete = async () => { await run(() => api.updateIncidentAction(name, completeIdx, { status: "Completed", completion_note: completeNote.trim() }), "Marked complete"); setCompleteIdx(null); setCompleteNote(""); };
  const remove = (idx) => run(() => api.deleteIncidentAction(name, idx), "Removed");

  const outstanding = actions.filter((a) => a.status === "Open" || a.status === "In Progress").length;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="incident-actions">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-slate-800 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5 text-slate-500" />Corrective actions
          {outstanding > 0 && <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5" data-testid="incident-actions-outstanding">{outstanding} open</span>}
        </div>
        {manage && isOpen && !adding && <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAdding(true)} data-testid="incident-action-add-toggle"><Plus className="h-3 w-3 mr-1" />Add</Button>}
      </div>

      {adding && (
        <div className="border border-slate-100 rounded-md p-2 mb-3 bg-slate-50/60 space-y-2" data-testid="incident-action-form">
          <Textarea value={form.action} onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))} placeholder="What needs to be done?" className="text-xs min-h-[54px]" data-testid="incident-action-text" />
          <Combobox testid="incident-action-assignee" options={staffOpts} value={form.assigned_to} onChange={(v) => setForm((f) => ({ ...f, assigned_to: v }))} placeholder="Assign to (optional)" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400">Due date</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="h-8 text-xs" data-testid="incident-action-due" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400">Priority</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="h-8 w-full text-xs rounded-md border border-slate-200 bg-white px-2" data-testid="incident-action-priority">
                {["Low", "Medium", "High"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-[11px] bg-slate-800 hover:bg-slate-900" disabled={busy} onClick={submitAdd} data-testid="incident-action-save">Save action</Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="incident-actions-list">
        {actions.length === 0 ? <div className="text-[11px] text-slate-400">No corrective actions yet.</div> :
          actions.map((a, i) => {
            const done = a.status === "Completed" || a.status === "Cancelled";
            const overdue = !done && a.due_date && String(a.due_date) < today;
            return (
              <div key={i} className="border border-slate-100 rounded-md p-2" data-testid={`incident-action-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-slate-700 whitespace-pre-wrap flex-1">{a.action}</div>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold shrink-0 ${P[a.priority] || P.Medium}`}>{a.priority}</span>
                </div>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px]">
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${S[a.status]}`} data-testid={`incident-action-status-${i}`}>{a.status}</span>
                  <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid={`incident-action-due-${i}`}>
                    {overdue && <AlertTriangle className="h-3 w-3" />}Due {a.due_date ? dayjs(a.due_date).format("D MMM YYYY") : "—"}{overdue ? " · overdue" : ""}
                  </span>
                  {a.assigned_to_name && <span className="text-slate-500">→ {a.assigned_to_name}</span>}
                </div>
                {a.completion_note && <div className="text-[10px] text-teal-700 mt-1">✓ {a.completion_note}</div>}
                {manage && (
                  <div className="flex gap-1.5 mt-2">
                    {!done && a.status === "Open" && <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={busy} onClick={() => setStatus(i, "In Progress")} data-testid={`incident-action-start-${i}`}>Start</Button>}
                    {!done && <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-teal-700 border-teal-200" disabled={busy} onClick={() => { setCompleteIdx(i); setCompleteNote(""); }} data-testid={`incident-action-complete-${i}`}><CheckCircle2 className="h-3 w-3 mr-1" />Complete</Button>}
                    {!done && <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-slate-500" disabled={busy} onClick={() => setStatus(i, "Cancelled")} data-testid={`incident-action-cancel-${i}`}>Cancel</Button>}
                    {isOpen && <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 ml-auto" disabled={busy} onClick={() => remove(i)} data-testid={`incident-action-delete-${i}`}><Trash2 className="h-3 w-3" /></Button>}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <Dialog open={completeIdx !== null} onOpenChange={(v) => !v && setCompleteIdx(null)}>
        <DialogContent data-testid="incident-action-complete-dialog">
          <DialogHeader><DialogTitle className="text-base">Complete corrective action</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Add an optional note on how this action was resolved.</p>
          <Textarea autoFocus value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} placeholder="Completion note (optional)" className="text-xs min-h-[70px]" data-testid="incident-action-complete-note" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCompleteIdx(null)}>Cancel</Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={complete} data-testid="incident-action-complete-confirm">Mark complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IncidentPhotos({ incidentName, photos }) {
  const [items, setItems] = useState(() => photos.map((p) => ({ name: p.file_name || "photo", url: null, blob: null, error: false })));
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let urls = [];
    let cancelled = false;
    (async () => {
      for (let i = 0; i < photos.length; i++) {
        try {
          const blob = await api.incidentPhoto(incidentName, i);
          if (cancelled) return;
          const url = URL.createObjectURL(blob); urls.push(url);
          setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, url, blob } : it));
        } catch {
          if (cancelled) return;
          setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, error: true } : it));
        }
      }
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [incidentName]); // eslint-disable-line

  const download = (it) => { if (!it.blob) return; const u = URL.createObjectURL(it.blob); const a = document.createElement("a"); a.href = u; a.download = it.name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); };

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="incident-photos">
        {items.map((it, i) => (
          <div key={i} className="relative group">
            {it.url ? (
              <button onClick={() => setLightbox(it)} data-testid={`incident-photo-${i}`}
                className="block w-full aspect-square rounded-md overflow-hidden border border-slate-200 hover:ring-2 hover:ring-red-300">
                <img src={it.url} alt={it.name} className="w-full h-full object-cover"
                  onLoad={(e) => { if (e.target.naturalWidth <= 1 || e.target.naturalHeight <= 1) setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, error: true, url: null } : x)); }}
                  onError={() => setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, error: true, url: null } : x))} />
              </button>
            ) : it.error ? (
              <div className="w-full aspect-square rounded-md border border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400 text-[9px] gap-1">
                <ImageOff className="h-4 w-4" /> can't preview
              </div>
            ) : (
              <div className="w-full aspect-square rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
              </div>
            )}
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="incident-photo-lightbox">
          <div className="max-w-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.name} className="max-h-[80vh] rounded-lg object-contain" />
            <div className="flex items-center justify-between mt-2 text-white text-xs">
              <span>{lightbox.name}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={() => download(lightbox)} data-testid="incident-photo-download"><Download className="h-3 w-3 mr-1" />Download</Button>
                <Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={() => setLightbox(null)}><X className="h-3 w-3 mr-1" />Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

